import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import WidgetChart from '../../../common/widgetChart/WidgetChart';
import { BopisLeadTimePerformanceItem } from '../../../../../hooks';
import { StorePerformanceInterval } from '../../../../../constants';
import { ColumnFlexContainer, RowFlexContainer } from '../../../../../stories/atoms/containerV2/ContainerV2';
import { isMultiDayInterval } from '../../../util';
import { WidgetCommonState } from '../../../common/widgetCommonState/WidgetCommonState';
import { WidgetMetricTile, WidgetMetricType } from '../../../common/metricTile/WidgetMetricTile';
import { homeTranslations } from '../../../../../translations';

type GraphContentProps = {
  customerPromise: number;
  interval: StorePerformanceInterval;
  items: BopisLeadTimePerformanceItem[];
};

export const GraphContent: FC<GraphContentProps> = ({ customerPromise, interval, items }) => {
  const { t } = useTranslation();
  const { bopisLeadTime: bopisLeadTimeTranslations } = homeTranslations;

  const itemValueResolver = (item: BopisLeadTimePerformanceItem) => {
    const { totalOrders, compliantOrders } = item;
    return totalOrders > 0 ? (compliantOrders / totalOrders) * 100 : 0;
  };

  return (
    <ColumnFlexContainer flex={1} data-testid="bopis-lead-time-graph-content" padding={[4]}>
      <RowFlexContainer margin={[0, 0, 2, 0]}>
        <WidgetMetricTile
          metricProperties={{
            type: WidgetMetricType.LegendMetric,
            properties: {
              metricValue: `${customerPromise.toFixed(2)}%`,
              metricTitle: t(
                bopisLeadTimeTranslations.customerPromise.key,
                bopisLeadTimeTranslations.customerPromise.defaultValue,
              ),
              legendColor: 'validationGreen',
            },
          }}
        />
      </RowFlexContainer>

      {!isMultiDayInterval(interval) && (
        <ColumnFlexContainer flex={1} padding={[4]} alignItems="center" justifyContent="center">
          <WidgetCommonState
            isNoData
            customNoDataText={t(
              bopisLeadTimeTranslations.graphNoDataText.key,
              bopisLeadTimeTranslations.graphNoDataText.defaultValue,
            )}
          />
        </ColumnFlexContainer>
      )}

      {isMultiDayInterval(interval) && (
        <ColumnFlexContainer flex={1}>
          <WidgetChart
            performanceItemsChartName={t(
              bopisLeadTimeTranslations.customerPromise.key,
              bopisLeadTimeTranslations.customerPromise.defaultValue,
            )}
            performanceItems={items}
            filteredPerformanceItems={items}
            forecastPerformanceItems={[]}
            itemValueResolver={itemValueResolver}
            interval={interval}
            yAxisFormatter={(value) => `${value.toFixed(0)}%`}
            tooltipFormatter={(value) => `${value.toFixed(2)}%`}
            showXAxis={true}
          />
        </ColumnFlexContainer>
      )}
    </ColumnFlexContainer>
  );
};
